/**
 * Layer Icons — standardized icons for the 3-Layer Information Synthesis Framework.
 *
 * IntentIcon:    Person silhouette (account_circle) — user-provided inputs.
 * FactualIcon:   Verified shield — independently sourced, third-party data.
 * SynthesisIcon: Two 4-pointed diamond stars (matches 44 Labs logo pattern) — AI analysis.
 *
 * All icons follow Lucide conventions: 24x24 viewBox, currentColor, accepts className/size/style.
 */

import type { SVGProps } from 'react';

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number | string;
}

/**
 * IntentIcon — Person silhouette in circle (Material: account_circle).
 * Used for: Project Inputs tab, Intent Layer badges, user-provided data markers.
 */
export function IntentIcon({ size = 24, className, style, ...props }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      style={style}
      {...props}
    >
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * FactualIcon — Verified shield with checkmark (Material: verified).
 * Used for: Project Facts tab, Factual Layer badges, third-party verified data.
 */
export function FactualIcon({ size = 24, className, style, ...props }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      style={style}
      {...props}
    >
      <path
        d="M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5l3.4-1.47 3.4 1.46 1.89-3.19 3.61-.82-.34-3.69L23 12zm-12.91 4.72l-3.8-3.81 1.48-1.48 2.32 2.33 5.85-5.87 1.48 1.48-7.33 7.35z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * SynthesisIcon — Two diamond stars matching 44 Labs brand.
 * Large star top-left, smaller star bottom-right.
 */
export function SynthesisIcon({ size = 24, className, style, ...props }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      {...props}
    >
      {/* Large star — top-left */}
      <path
        d="M9 2 L11.5 7.5 L17 10 L11.5 12.5 L9 18 L6.5 12.5 L1 10 L6.5 7.5 Z"
        fill="currentColor"
        stroke="none"
      />
      {/* Small star — bottom-right */}
      <path
        d="M18 14 L19.5 17 L22.5 18.5 L19.5 20 L18 23 L16.5 20 L13.5 18.5 L16.5 17 Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}
